import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useChatSidebarCollapseOptional } from '../../../contexts/ChatSidebarCollapseContext';
import { useConfirmationsOptional } from '../../../contexts/ConfirmationsContext';
import { useOnboardingModals } from '../../../providers';
import { ReactComponent as ConfirmationShowArrowIcon } from '../../../icons/confirmation-show-arrow.svg';
import { ReactComponent as PenInitialsIcon } from '../../../icons/pen-initials.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as ClipboardListIcon } from '../../../icons/clipboard-list.svg';
import { createConfirmationCanvasAsset } from '../canvas/dsl-components/confirmationCanvasHelper';
import { generateMessageColors } from './threadUtils';
import { OnboardingModal } from '../../onboarding/OnboardingModal';
import './confirmation-notifications.scss';

interface Confirmation {
  id: string;
  task: string;
  method_name: string;
  method_input: Record<string, any>;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'ERROR';
  created_at: string;
  thread_id: string;
  notification_text?: string;
}

interface ConfirmationNotificationsProps {
  threadId: string | null;
  inMiniMode?: boolean;
  predominantColour?: string;
  onNavigateToFullscreen?: () => void;
  onExpandedChange?: (expanded: boolean, hasMultiple: boolean) => void;
}

const getConfirmationMessage = (
  methodName: string,
  notificationText?: string,
): string => {
  // Use custom notification text if provided
  if (notificationText) {
    return notificationText;
  }

  // Fallback to predefined messages
  const messages: Record<string, string> = {
    executeSendDraft: 'I need you to approve an email',
    executeCreateCalendarEvent: 'I need you to approve a calendar event',
    executeUpdateCalendarEvent: 'I need you to approve a calendar update',
    executeDeleteCalendarEvent: 'I need you to approve a calendar deletion',
    startCampaign: 'I need you to approve starting a campaign',
    deleteCampaign: 'I need you to approve deleting a campaign',
    deleteProfiles: 'I need you to approve deleting profiles',
    approveSourcingStrategy: 'I need you to approve a sourcing strategy',
    approvePendingProspectsForCampaign: 'I need you to approve prospects',
    approveEmailSequences: 'I need you to approve email sequences',
  };

  return messages[methodName] || 'I need you to approve something';
};

const getConfirmationTitle = (methodName: string): string => {
  const titles: Record<string, string> = {
    executeSendDraft: 'Email Approval',
    executeCreateCalendarEvent: 'Calendar Event Approval',
    executeUpdateCalendarEvent: 'Calendar Update Approval',
    executeDeleteCalendarEvent: 'Calendar Deletion Approval',
    startCampaign: 'Start Campaign Approval',
    deleteCampaign: 'Delete Campaign Approval',
    deleteProfiles: 'Delete Profiles Approval',
    approveSourcingStrategy: 'Sourcing Strategy Approval',
    approvePendingProspectsForCampaign: 'Prospects Approval',
    approveEmailSequences: 'Email Sequence Approval',
  };

  return titles[methodName] || 'Action Needs Confirmation';
};

const ConfirmationNotifications: React.FC<ConfirmationNotificationsProps> = ({
  threadId,
  inMiniMode,
  predominantColour,
  onNavigateToFullscreen,
  onExpandedChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInitialsModal, setShowInitialsModal] = useState(false);
  const prevCountRef = useRef(0);
  const { getAccessTokenSilently } = useAuth0();
  const sidebarCollapseContext = useChatSidebarCollapseOptional();
  const confirmationsContext = useConfirmationsOptional();
  const { hasSeenModal, markAsSeen } = useOnboardingModals();

  // Get confirmations from centralized context
  const confirmations = (confirmationsContext?.confirmations ||
    []) as Confirmation[];

  // Expand the sidebar when a new confirmation arrives (if sidebar is collapsed)
  // Keep confirmations collapsed by default - user can expand manually
  useEffect(() => {
    const isNewConfirmation = confirmations.length > prevCountRef.current;

    if (
      isNewConfirmation &&
      confirmations.length > 0 &&
      sidebarCollapseContext?.isCollapsed
    ) {
      sidebarCollapseContext.expandSidebar();
    }

    prevCountRef.current = confirmations.length;
  }, [confirmations.length, sidebarCollapseContext]);

  // Notify parent of expanded state changes
  useEffect(() => {
    const hasMultiple = confirmations.length > 1;
    onExpandedChange?.(isExpanded && hasMultiple, hasMultiple);
  }, [isExpanded, confirmations.length, onExpandedChange]);

  const handleArrowClick = async (confirmation: Confirmation) => {
    if (inMiniMode && onNavigateToFullscreen) {
      onNavigateToFullscreen();
    } else if (threadId) {
      await createConfirmationCanvasAsset({
        confirmationIds: [confirmation.id],
        threadId,
        getAccessTokenSilently,
        title: getConfirmationTitle(confirmation.method_name),
      });
    }
  };

  if (confirmations.length === 0) {
    return null;
  }

  // Sort by created_at ascending (oldest first, newest last)
  const sortedConfirmations = [...confirmations].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  const colors = predominantColour
    ? generateMessageColors(predominantColour)
    : null;

  const hasMultiple = confirmations.length > 1;

  // Show dot on first/only visible notification if modal not seen
  const showOnboardingDot = !hasSeenModal('initials');

  const renderOnboardingDot = () =>
    showOnboardingDot ? (
      <button
        className='onboarding-dot'
        onClick={(e) => {
          e.stopPropagation();
          setShowInitialsModal(true);
        }}
        aria-label='Learn about initials'
      />
    ) : null;

  return (
    <div className='confirmation-notifications-container'>
      {/* Collapsed summary view for multiple confirmations */}
      {hasMultiple && !isExpanded && (
        <div
          className='confirmation-notification summary-notification'
          onClick={() => setIsExpanded(true)}
          style={
            colors
              ? {
                  background: colors.background,
                  borderColor: colors.borderColor,
                  boxShadow: colors.boxShadow,
                }
              : undefined
          }
        >
          {renderOnboardingDot()}
          <div className='icon-wrapper'>
            <ClipboardListIcon />
            <div className='urgency-icon'>
              <span>!</span>
            </div>
          </div>
          <p>I need you to initial {confirmations.length} things</p>
          <div
            className='arrow-right-wrapper'
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
          >
            <ConfirmationShowArrowIcon />
          </div>
        </div>
      )}

      {/* Expanded list or single confirmation */}
      {(!hasMultiple || isExpanded) &&
        sortedConfirmations.map((confirmation, index) => (
          <div
            key={confirmation.id}
            className='confirmation-notification'
            onClick={() => handleArrowClick(confirmation)}
            style={
              colors
                ? {
                    background: colors.background,
                    borderColor: colors.borderColor,
                    boxShadow: colors.boxShadow,
                  }
                : undefined
            }
          >
            {index === 0 && !hasMultiple && renderOnboardingDot()}
            <div className='icon-wrapper'>
              <PenInitialsIcon />
            </div>
            <p>
              {getConfirmationMessage(
                confirmation.method_name,
                confirmation.notification_text,
              )}
            </p>
            <div
              className={`arrow-right-wrapper${hasMultiple ? ' multi-view' : ''}`}
            >
              <ConfirmationShowArrowIcon />
            </div>
          </div>
        ))}

      {/* Collapse toggle at bottom when expanded */}
      {hasMultiple && isExpanded && (
        <div
          className='confirmation-notification summary-notification'
          style={
            colors
              ? {
                  background: colors.background,
                  borderColor: colors.borderColor,
                  boxShadow: colors.boxShadow,
                }
              : undefined
          }
        >
          <div className='icon-wrapper'>
            <ClipboardListIcon />
            <div className='urgency-icon'>
              <span>!</span>
            </div>
          </div>
          <p>I need you to initial {confirmations.length} things</p>
          <div
            className='arrow-right-wrapper'
            onClick={() => setIsExpanded(false)}
          >
            <ChevronDownIcon />
          </div>
        </div>
      )}

      <OnboardingModal
        modalKey='initials'
        title='Big decisions'
        imageSrc='/onboarding-modals/initials.png'
        primaryButtonText='Got it'
        controlled={true}
        show={showInitialsModal}
        onClose={() => {
          setShowInitialsModal(false);
          markAsSeen('initials');
        }}
      >
        <p>
          Growing your business means asking Dreamies to do things they need
          approval for like starting a new campaign or booking a domain.
        </p>
        <p>
          For these things Dreamies have a hard requirement that you "Initial"
          the action - they won't be able to proceed without it.
        </p>
        <p>
          Any initials will now always appear above the chat for you to keep up
          to date with.
        </p>
      </OnboardingModal>
    </div>
  );
};

export default ConfirmationNotifications;
